/* POST_KIOSK × DONATION journey — thin shell (GC-P1-05). */

import { useMemo } from 'react';
import { JourneyShell } from './JourneyShell.js';
import { Card } from '../../shared/ui/Card.js';
import { Button } from '../../shared/ui/Button.js';
import { PostPayCta } from '../post-pay/PostPayCta.js';
import { usePostKioskDonationJourney } from './hooks/usePostKioskDonationJourney.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { resolvePostAuthFallbackPath } from '../../navigation/navigationEngine.js';
import {
  usePublishHierarchicalBackFlow,
  type HierarchicalBackFlowPublishInput,
} from '../../shared/context/HierarchicalBackFlowContext.js';

export function PostKioskDonationJourney(): JSX.Element {
  const screen = usePostKioskDonationJourney();
  const tenantCode = useTenantCode();
  const shopFallback = resolvePostAuthFallbackPath(tenantCode);
  // Terminal success/pending: content bar off; fixed shell ← → shop via hierarchical.
  // Failure/expired: content + publish escape to shop.
  const isTerminalSuccess =
    screen.renderStatus === 'COMPLETED' || screen.renderStatus === 'PENDING';
  const backTo = isTerminalSuccess ? null : shopFallback;
  const publishInput = useMemo((): HierarchicalBackFlowPublishInput | null => {
    if (screen.renderStatus === 'COMPLETED' || screen.renderStatus === 'PENDING') {
      return null;
    }
    return {
      flowKind: 'donation',
      flowStep:
        screen.renderStatus === 'FAILED'
          ? 'post-kiosk-failed'
          : 'post-kiosk-expired',
      paymentActive: false,
      stepBackTo: shopFallback,
      onStepBack: null,
    };
  }, [screen.renderStatus, shopFallback]);
  usePublishHierarchicalBackFlow(publishInput);

  return (
    <JourneyShell
      title={screen.title}
      subtitle={screen.subtitle}
      context="POST_KIOSK_DONATION"
      contentWidth="narrow"
      backTo={backTo}
      backLabel={backTo !== null ? screen.t('checkout.success.backToShop') : undefined}
    >
      <Card padded>
        <div className="flex flex-col gap-2">
          {screen.renderStatus === 'COMPLETED' ? (
            <>
              <h2 className="text-lg font-semibold">
                {screen.projectName ?? screen.t('postKioskDonation.thanksHeading')}
              </h2>
              {screen.projectDescription !== null && screen.projectDescription !== undefined ? (
                <p className="text-sm text-[var(--color-on-surface-muted)]">
                  {screen.projectDescription}
                </p>
              ) : (
                <p className="text-sm text-[var(--color-on-surface-muted)]">
                  {screen.t('postKioskDonation.thanksDescription')}
                </p>
              )}
            </>
          ) : null}
          {screen.renderStatus === 'PENDING' ? (
            <p className="text-sm text-[var(--color-on-surface-muted)]" role="status">
              {screen.t('postKioskDonation.pendingDescription')}
            </p>
          ) : null}
          {screen.renderStatus === 'FAILED' ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-[var(--color-danger)]">
                {screen.t('postKioskDonation.failedDescription')}
              </p>
              <Button intent="primary" size="md" block onClick={screen.onRetry}>
                {screen.t('postKioskDonation.retry')}
              </Button>
            </div>
          ) : null}
          {screen.renderStatus === 'EXPIRED' ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-[var(--color-on-surface-muted)]">
                {screen.t('postKioskDonation.expiredDescription')}
              </p>
              <Button intent="primary" size="md" block onClick={screen.onRefresh}>
                {screen.t('postKioskDonation.refresh')}
              </Button>
            </div>
          ) : null}
        </div>
      </Card>

      {screen.renderStatus === 'COMPLETED' && screen.paymentId.length > 0 ? (
        <PostPayCta paymentId={screen.paymentId} onContinue={screen.onPostPayContinue} />
      ) : null}
    </JourneyShell>
  );
}
